import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { PreKSettings as KSettings, DiagnosticAssessmentResult } from '../../types/kAssessmentTypes';
import { useKTTS, K_QUESTION_SCREEN_AUDIO } from '../../hooks/useKAudio';
import { useAuth } from '../../context/AuthContext';
import { progressQueue } from '../../services/ProgressQueueService';
import { recordPageTransition, completePreKAssessment } from '../../config/api';
import { FactStatus } from '../../types/progress';
import { TRACK_RANGES } from '../../types/constants';
import { NUMERAL_FONT_STYLE } from '../../styles/design-system/typography';
import WarmupTransitionScreen from './WarmupTransitionScreen';
import AnswerBlank from '../common/AnswerBlank';
import { AnswerChoicesGrid, AssessmentHeader } from '../PreKAssessment/shared';
import ListenButton from '../common/ListenButton';

interface AdditionWithin5QuestionScreenProps {
  settings: KSettings;
  onComplete: (results: DiagnosticAssessmentResult) => void;
}

// All valid addition facts within 5 (operand1 ≤ operand2, sum ≤ 5)
const ADDITION_FACTS = [
  { operand1: 0, operand2: 0, sum: 0 },  // Fact 1685
  { operand1: 0, operand2: 1, sum: 1 },  // Fact 1686
  { operand1: 0, operand2: 2, sum: 2 },  // Fact 1687
  { operand1: 0, operand2: 3, sum: 3 },  // Fact 1688
  { operand1: 0, operand2: 4, sum: 4 },  // Fact 1689
  { operand1: 0, operand2: 5, sum: 5 },  // Fact 1690
  { operand1: 1, operand2: 1, sum: 2 },  // Fact 1691
  { operand1: 1, operand2: 2, sum: 3 },  // Fact 1692
  { operand1: 1, operand2: 3, sum: 4 },  // Fact 1693
  { operand1: 1, operand2: 4, sum: 5 },  // Fact 1694
  { operand1: 2, operand2: 2, sum: 4 },  // Fact 1695
  { operand1: 2, operand2: 3, sum: 5 },  // Fact 1696
];

const AdditionWithin5QuestionScreen: React.FC<AdditionWithin5QuestionScreenProps> = ({ settings, onComplete }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [wallClockTimeRemaining, setWallClockTimeRemaining] = useState(60000); // 60 seconds wall clock limit
  const [wallClockStarted, setWallClockStarted] = useState(false); // Flag to track when wall clock starts
  const [activeTimeElapsed, setActiveTimeElapsed] = useState(0); // Track actual active thinking time
  const [questionTimeRemaining, setQuestionTimeRemaining] = useState(6000); // 6 seconds per question
  const [questionTimerActive, setQuestionTimerActive] = useState(false); // Per-question timer
  const [isWarmup, setIsWarmup] = useState(true);
  const [warmupCount, setWarmupCount] = useState(0);
  const [showTransitionScreen, setShowTransitionScreen] = useState(false);
  const [currentFactIndex, setCurrentFactIndex] = useState<number | null>(null);
  const [waitingForCorrectAnswer, setWaitingForCorrectAnswer] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [selectedAnswerValue, setSelectedAnswerValue] = useState<number | null>(null);

  // Auth context for user ID
  const { user, updateUserField } = useAuth();

  // Timing references
  const testStartTime = useRef<number>(Date.now());
  const questionStartTime = useRef<number>(Date.now());
  const timerActiveStartTime = useRef<number>(0);
  const accumulatedTimerTime = useRef<number>(0);
  const questionActiveStartRef = useRef<number | null>(null);
  const questionAccumulatedTimeRef = useRef<number>(0);
  const hasCompletedRef = useRef<boolean>(false);
  const isFirstQuestionRef = useRef(true);

  // TTS integration
  const { playTTSWithCallback, isTTSPlaying, playChime } = useKTTS();

  // Fact index to fact mapping (0-11 map to facts 1685-1696)
  const factIndexToFact = useCallback((index: number): string => {
    const [startFact] = TRACK_RANGES.TRACK_K_ADDITION_WITHIN_5;
    return `FACT${startFact + index}`; // Maps 0-11 to facts 1685-1696
  }, []);

  // Determine fact status based on correctness and response time (targeting 6s fluency for addition within 5)
  const determineFactStatus = useCallback((isCorrect: boolean, timeSpentMs: number): FactStatus => {
    if (!isCorrect) {
      return 'learning';
    }

    const timeSpentSeconds = timeSpentMs / 1000;

    if (timeSpentSeconds > 6) {
      return 'accuracyPractice';  // Need to work on speed
    } else {
      return 'mastered';  // Already at target 6s fluency
    }
  }, []);

  // Generate random fact index (0-11)
  const generateRandomFactIndex = useCallback(() => {
    return Math.floor(Math.random() * ADDITION_FACTS.length);
  }, []);

  // Initialize first fact index
  useEffect(() => {
    if (currentFactIndex === null) {
      const factIndex = generateRandomFactIndex();
      setCurrentFactIndex(factIndex);
    }
  }, [currentFactIndex, generateRandomFactIndex]);

  // Generate answer choices (target + 3 other options from 0-5)
  const generateAnswerChoices = useCallback((target: number) => {
    const choices = [target];
    const used = new Set([target]);

    // Add 3 other numbers between 0-5, avoiding target
    while (choices.length < 4) {
      const candidate = Math.floor(Math.random() * 6); // 0-5
      if (!used.has(candidate)) {
        choices.push(candidate);
        used.add(candidate);
      }
    }

    // Shuffle the choices
    return choices.sort(() => Math.random() - 0.5);
  }, []);

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [displayOrder, setDisplayOrder] = useState<'normal' | 'swapped'>('normal');

  // Update answer choices and display order when fact index changes
  useEffect(() => {
    if (currentFactIndex !== null) {
      const currentFact = ADDITION_FACTS[currentFactIndex];
      setAnswerChoices(generateAnswerChoices(currentFact.sum));
      setDisplayOrder(Math.random() < 0.5 ? 'normal' : 'swapped');
      setHasAnswered(false);
    }
  }, [currentFactIndex, generateAnswerChoices]);

  // Set track ID and report page transition when component mounts
  useEffect(() => {
    sessionStorage.setItem('activeTrackId', 'TRACK_K_ADDITION_WITHIN_5');

    if (user?.userId) {
      recordPageTransition({
        userId: user.userId,
        trackId: 'TRACK_K_ADDITION_WITHIN_5',
        page: 'assessment'
      }).catch((error: any) => {
        console.error('Error recording page transition:', error);
      });
    }
  }, [user?.userId]);

  // Update question start time when moving to test phase
  useEffect(() => {
    if (!isWarmup) {
      questionStartTime.current = Date.now();
      accumulatedTimerTime.current = 0;
      timerActiveStartTime.current = 0;
      // Reset per-question timing
      questionAccumulatedTimeRef.current = 0;
    }
  }, [isWarmup, currentFactIndex]);

  // Track timer active time
  useEffect(() => {
    if (isWarmup) return;

    if (timerActive) {
      timerActiveStartTime.current = Date.now();
      questionActiveStartRef.current = Date.now();
    } else {
      if (timerActiveStartTime.current > 0) {
        const activeTime = Date.now() - timerActiveStartTime.current;
        accumulatedTimerTime.current += activeTime;
        timerActiveStartTime.current = 0;
      }
      if (questionActiveStartRef.current) {
        const questionActiveTime = Date.now() - questionActiveStartRef.current;
        questionAccumulatedTimeRef.current += questionActiveTime;
        questionActiveStartRef.current = null;
      }
    }
  }, [timerActive, isWarmup]);

  // Wall clock timer countdown (strict 60s limit including VOs and transitions)
  useEffect(() => {
    // Don't run during warmup or before wall clock starts
    if (isWarmup || !wallClockStarted || wallClockTimeRemaining <= 0) {
      if (wallClockTimeRemaining <= 0 && wallClockStarted && !hasCompletedRef.current) {
        // Wall clock expired - end test immediately
        hasCompletedRef.current = true;
        handleTestComplete({
          userId: user?.userId || '',
          testResults: [],
          recommendedStartSkill: 1,
          placementReason: 'Time limit reached (60 seconds)',
          completedAt: new Date(),
          totalQuestions,
          correctAnswers,
          activeTimeElapsed
        });
      }
      return;
    }

    const timer = setTimeout(() => {
      setWallClockTimeRemaining(prev => Math.max(0, prev - 100));
    }, 100);

    return () => clearTimeout(timer);
  }, [wallClockTimeRemaining, wallClockStarted, isWarmup, user?.userId, totalQuestions, correctAnswers, activeTimeElapsed]);

  // Active time tracking (for analytics - no limit)
  useEffect(() => {
    // Only track active time during test phase when timer is active
    if (isWarmup || !timerActive) return;

    const timer = setTimeout(() => {
      setActiveTimeElapsed(prev => prev + 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [timerActive, isWarmup, activeTimeElapsed]);

  // Wrapper to handle page transition reporting before completing
  const handleTestComplete = useCallback(async (results: DiagnosticAssessmentResult) => {
    if (user?.userId) {
      try {
        await recordPageTransition({
          userId: user.userId,
          trackId: 'TRACK_K_ADDITION_WITHIN_5',
          page: 'other'
        });
      } catch (error) {
        console.error('Error recording final page transition:', error);
      }

      // Save K assessment data to database and sync focusTrack
      try {
        const result = await completePreKAssessment(user.userId, 'TRACK_K_ADDITION_WITHIN_5', {
          totalQuestions: results.totalQuestions || 0,
          correctAnswers: results.correctAnswers || 0,
          activeTimeElapsed: results.activeTimeElapsed || 0
        });
        if (result.focusTrack) {
          updateUserField({ focusTrack: result.focusTrack });
        }
      } catch (error) {
        console.error('Failed to save K assessment data:', error);
      }
    }
    onComplete(results);
  }, [user?.userId, onComplete, updateUserField]);

  // Play TTS when fact index is set and TTS hasn't played yet
  // Only play instruction on first warmup question OR every 3rd test question
  useEffect(() => {
    if (currentFactIndex !== null && !ttsHasPlayed) {
      const shouldPlayAudio = isWarmup
        ? warmupCount === 0
        : currentQuestionIndex === 0;

      if (settings.speechEnabled && shouldPlayAudio) {
        playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.additionWithin5.instruction, () => {
          if (!isWarmup) {
            setTimerActive(true); // Start timer only after TTS completes (test phase only)
            setQuestionTimerActive(true); // Start question timer
          }
          setTtsHasPlayed(true);
        }).catch(() => {
          if (!isWarmup) {
            setTimerActive(true);
            setQuestionTimerActive(true); // Start question timer
          }
          setTtsHasPlayed(true);
        });
      } else {
        // No audio needed - start timers immediately if test phase
        if (!isWarmup) {
          setTimerActive(true);
          setQuestionTimerActive(true);
        }
        setTtsHasPlayed(true);
      }
    }
  }, [currentFactIndex, ttsHasPlayed, settings.speechEnabled, playTTSWithCallback, isWarmup, warmupCount, currentQuestionIndex]);

  // Reset state for new question
  const resetQuestionState = useCallback((callback?: () => void) => {
    isFirstQuestionRef.current = false;
    setTimerActive(false);
    setQuestionTimerActive(false); // Pause question timer during transitions
    setQuestionTimeRemaining(6000); // Reset question timer to 6 seconds
    setWrongAnswer(null);
    setTtsHasPlayed(false);
    setHasAnswered(false);
    setSelectedAnswerValue(null);

    // Execute callback after state reset if provided
    if (callback) {
      setTimeout(callback, 50);
    }
  }, []);

  // Generate next fact index with different value
  const generateNextFactIndex = useCallback(() => {
    let newFactIndex;
    do {
      newFactIndex = generateRandomFactIndex();
    } while (currentFactIndex !== null && newFactIndex === currentFactIndex);

    return newFactIndex;
  }, [currentFactIndex, generateRandomFactIndex]);

  // Store generateNextFactIndex in a ref to avoid dependency issues
  const generateNextFactIndexRef = useRef(generateNextFactIndex);
  generateNextFactIndexRef.current = generateNextFactIndex;

  // Handle warmup progression
  const advanceWarmupQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
    }

    const newWarmupCount = warmupCount + 1;

    // Check if warmup is complete (fixed 3 questions)
    if (newWarmupCount >= 3) {
      // Show transition screen instead of immediately starting test
      setShowTransitionScreen(true);
      return;
    }

    setWarmupCount(newWarmupCount);
    resetQuestionState(() => setCurrentFactIndex(generateNextFactIndex()));
  }, [waitingForCorrectAnswer, warmupCount, generateNextFactIndex, resetQuestionState]);

  // Handle transition screen completion - start the actual test
  const handleTransitionComplete = useCallback(() => {
    setShowTransitionScreen(false);
    setIsWarmup(false);
    setWarmupCount(0);
    setCurrentQuestionIndex(0);
    setWallClockStarted(true);
    resetQuestionState(() => setCurrentFactIndex(generateNextFactIndexRef.current()));
  }, [resetQuestionState]);

  // Handle test progression
  const advanceTestQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      resetQuestionState(() => setCurrentFactIndex(generateNextFactIndexRef.current()));
      return;
    }

    setCorrectAnswers(correctAnswers + 1);
    setTotalQuestions(totalQuestions + 1);
    const nextIndex = currentQuestionIndex + 1;
    setCurrentQuestionIndex(nextIndex);
    resetQuestionState(() => setCurrentFactIndex(generateNextFactIndex()));
  }, [waitingForCorrectAnswer, correctAnswers, totalQuestions, currentQuestionIndex, generateNextFactIndex, resetQuestionState]);

  const handleAnswerClick = useCallback((selectedAnswer: number) => {
    if (currentFactIndex === null) return;

    const currentFact = ADDITION_FACTS[currentFactIndex];

    // If waiting for correct answer, only allow correct selection
    if (waitingForCorrectAnswer && selectedAnswer !== currentFact.sum) return;

    // If not waiting and already answered, don't allow more clicks
    if (!waitingForCorrectAnswer && hasAnswered) return;

    // Force timer to be active since user has responded
    if (!isWarmup) {
      setTimerActive(true);
      setQuestionTimerActive(true); // Ensure question timer is active
    }
    setTtsHasPlayed(true);
    setHasAnswered(true);
    setSelectedAnswerValue(selectedAnswer);

    const isCorrect = selectedAnswer === currentFact.sum;

    // Calculate per-question active response time for test questions
    let timerActiveResponseTime = 0;
    if (!isWarmup) {
      timerActiveResponseTime = questionAccumulatedTimeRef.current + (questionActiveStartRef.current ? Date.now() - questionActiveStartRef.current : 0);
    }

    // Report progress data for test questions
    if (!isWarmup && user?.userId && !waitingForCorrectAnswer) {
      try {
        const factId = factIndexToFact(currentFactIndex);
        const factStatus = determineFactStatus(isCorrect, timerActiveResponseTime);

        progressQueue.enqueueProgressUpdate(
          user.userId,
          {
            facts: {
              [factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: timerActiveResponseTime,
                status: factStatus
              }
            }
          }
        );
      } catch (error) {
        console.error('Error reporting K addition within 5 progress:', error);
      }
    }

    // Handle answer logic with immediate audio feedback
    if (isWarmup) {
      if (isCorrect) {
        playChime().then(() => {
          setTimeout(() => {
            advanceWarmupQuestion();
          }, 300);
        });
      } else {
        setWrongAnswer(selectedAnswer);
        setTimeout(() => setWrongAnswer(null), 1000);

        // Replay instruction after boop
        if (settings.speechEnabled) {
          setTimeout(() => {
            playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.additionWithin5.instruction).catch(() => {});
          }, 300);
        }

        setWaitingForCorrectAnswer(true);
        setHasAnswered(false); // Reset so user can try again
      }
    } else {
      // Quiz mode: neutral feedback with chime for all answers, same timing
      if (!waitingForCorrectAnswer && !isCorrect) {
        setTotalQuestions(totalQuestions + 1);
      }

      playChime().then(() => {
        setTimeout(() => {
          if (isCorrect) {
            advanceTestQuestion();
          } else {
            const nextIndex = currentQuestionIndex + 1;
            setCurrentQuestionIndex(nextIndex);
            resetQuestionState(() => setCurrentFactIndex(generateNextFactIndexRef.current()));
          }
        }, 300); // 300ms delay for all answers
      });
    }
  }, [
    currentFactIndex, waitingForCorrectAnswer, hasAnswered, isWarmup, timerActive,
    user, isTTSPlaying, settings.speechEnabled, factIndexToFact, determineFactStatus,
    playChime, playTTSWithCallback, advanceWarmupQuestion, advanceTestQuestion,
    totalQuestions, currentQuestionIndex, resetQuestionState
  ]);

  // 6-second per-question countdown timer
  useEffect(() => {
    // Don't run question timer during warmup, when not active, or when already answered
    if (isWarmup || !questionTimerActive || questionTimeRemaining <= 0 || hasAnswered) {
      if (questionTimeRemaining <= 0 && questionTimerActive && !isWarmup && !hasAnswered) {
        // Question time expired - auto advance without showing correct answer
        if (waitingForCorrectAnswer) {
          // If waiting for correct answer, just advance
          setWaitingForCorrectAnswer(false);
          const nextIndex = currentQuestionIndex + 1;
          setCurrentQuestionIndex(nextIndex);
          resetQuestionState(() => setCurrentFactIndex(generateNextFactIndexRef.current()));
        } else {
          // Count as an incorrect attempt and advance
          setTotalQuestions(totalQuestions + 1);

          // Report progress data for timeout (treat as incorrect answer)
          if (user?.userId && currentFactIndex !== null) {
            try {
              const factId = factIndexToFact(currentFactIndex);
              const factStatus = determineFactStatus(false, 6000); // Use full question time as timeout response time

              progressQueue.enqueueProgressUpdate(
                user.userId,
                {
                  facts: {
                    [factId]: {
                      attempts: 1,
                      correct: 0, // Timeout is incorrect
                      timeSpent: questionAccumulatedTimeRef.current + (questionActiveStartRef.current ? Date.now() - questionActiveStartRef.current : 0), // Actual time spent on this question
                      status: factStatus
                    }
                  }
                }
              );
            } catch (error) {
              console.error('Error reporting K addition within 5 timeout progress:', error);
            }
          }

          const nextIndex = currentQuestionIndex + 1;
          setCurrentQuestionIndex(nextIndex);
          resetQuestionState(() => setCurrentFactIndex(generateNextFactIndexRef.current()));
        }
      }
      return;
    }

    const timer = setTimeout(() => {
      setQuestionTimeRemaining(prev => prev - 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [questionTimeRemaining, questionTimerActive, isWarmup, waitingForCorrectAnswer, hasAnswered]);

  if (currentFactIndex === null) {
    return <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
      <div className="text-2xl">Loading...</div>
    </div>;
  }

  // Show transition screen after warmup completes
  if (showTransitionScreen) {
    return (
      <WarmupTransitionScreen
        onComplete={handleTransitionComplete}
        speechEnabled={settings.speechEnabled}
      />
    );
  }

  const currentFact = ADDITION_FACTS[currentFactIndex];

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header */}
      <AssessmentHeader
        isWarmup={isWarmup}
        showTimer={true}
        wallClockTimeRemaining={wallClockTimeRemaining}
        wallClockStarted={wallClockStarted}
        isTTSPlaying={isTTSPlaying}
        timerHideable={true}
      />

      {/* Listen Button */}
      {settings.speechEnabled && (
        <div className="flex justify-center pt-4">
          <ListenButton
            onClick={() => playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.additionWithin5.instruction)}
            disabled={hasAnswered}
            isPlaying={isTTSPlaying}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-4 pb-8 gap-8">
        {/* Addition Problem Display */}
        <motion.div
          initial={isFirstQuestionRef.current ? { scale: 0, opacity: 0 } : { opacity: 0 }}
          animate={isFirstQuestionRef.current ? { scale: 1, opacity: 1 } : { opacity: 1 }}
          transition={isFirstQuestionRef.current ? { duration: 0.4 } : { duration: 0.15 }}
          className="text-6xl lg:text-7xl font-bold text-gray-800"
          style={NUMERAL_FONT_STYLE}
        >
          {displayOrder === 'normal'
            ? <>{currentFact.operand1} + {currentFact.operand2} = <AnswerBlank /></>
            : <>{currentFact.operand2} + {currentFact.operand1} = <AnswerBlank /></>
          }
        </motion.div>

        {/* Answer Choices - 2x2 grid */}
        <AnswerChoicesGrid
          choices={answerChoices}
          correctAnswer={currentFact.sum}
          wrongAnswer={wrongAnswer}
          hasAnswered={hasAnswered}
          waitingForCorrectAnswer={waitingForCorrectAnswer}
          disabled={false}
          onChoiceClick={handleAnswerClick}
          selectedAnswer={selectedAnswerValue}
          neutralFeedbackMode={!isWarmup}
          minimalAnimation={!isFirstQuestionRef.current}
        />
      </div>
    </div>
  );
};

export default AdditionWithin5QuestionScreen;
